import { useState } from 'react';
import { AlertTriangle, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useResendNotification } from '@/lib/notifications/use-notifications';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { queueResend, type ResendChannel, type ResendStatus } from './use-resend-offer';
import { PnrPrintDialog, type PnrPrintKind } from './pnr-print-sheet';
import { UpdateContactDialog } from './update-contact-dialog';

/**
 * The paper-and-comms actions of one PNR, as state + handlers: the card/toolbar/bar
 * variants below and the passenger card's «Más acciones» menu render the SAME actions.
 * `status` is the inline resend outcome; `overlays` are the print sheet and the contact
 * dialog — both must stay mounted wherever the trigger lives.
 */
export function usePnrUtilities(group: DisruptionPnrGroupRow, hasHotelReservation: boolean) {
  const { t } = useTranslation('pnrActions');
  const resend = useResendNotification(group.caseUrn);
  const [contactOpen, setContactOpen] = useState(false);
  /** Which sheet is on screen, or null. One slot — two sheets can never stack. */
  const [printKind, setPrintKind] = useState<PnrPrintKind | null>(null);
  const [resendStatus, setResendStatus] = useState<ResendStatus | null>(null);

  const hasFlight = !!group.assignedFlight || !!group.ticketNumber;
  // The pack degrades to whatever exists, so one assigned thing is enough to
  // make it worth printing — it is only meaningless when the PNR has nothing.
  const hasAnythingToPrint = hasFlight || hasHotelReservation;

  const runResend = (channel?: ResendChannel): void => {
    setResendStatus(null);
    queueResend(resend, group.subCaseUrn, channel, t, {
      noChannelNote: t('utility.toast.preferredChannelNote'),
      onStatus: setResendStatus,
    });
  };

  // The current state is read for every channel. Voice only offers DTMF
  // acceptance when the passenger actually has an open proposal.
  const runCall = (): void => {
    setResendStatus(null);
    queueResend(resend, group.subCaseUrn, 'VOICE', t, {
      noChannelNote: '',
      queuedTitle: t('utility.toast.calling'),
      onStatus: setResendStatus,
    });
  };

  const status = resendStatus ? (
    <div
      className={`mt-2 flex items-center gap-1.5 text-xs ${
        resendStatus.tone === 'success'
          ? 'text-emerald-700 dark:text-emerald-400'
          : resendStatus.tone === 'warning'
            ? 'text-amber-700 dark:text-amber-400'
            : 'text-destructive'
      }`}
      role={resendStatus.tone === 'error' ? 'alert' : 'status'}
      data-testid="pnr-utility-resend-status"
    >
      {resendStatus.tone === 'success' ? (
        <Check className="h-3 w-3" aria-hidden />
      ) : (
        <AlertTriangle className="h-3 w-3" aria-hidden />
      )}
      <span>
        <span className="font-medium">{resendStatus.title}</span>
        {resendStatus.description ? ` — ${resendStatus.description}` : null}
      </span>
    </div>
  ) : null;

  const overlays = (
    <>
      <PnrPrintDialog group={group} kind={printKind} onClose={() => setPrintKind(null)} />
      {contactOpen ? <UpdateContactDialog key={group.subCaseUrn} group={group} open onClose={() => setContactOpen(false)} /> : null}
    </>
  );

  return {
    resendPending: resend.isPending,
    runResend,
    runCall,
    openContact: () => setContactOpen(true),
    print: (kind: PnrPrintKind) => setPrintKind(kind),
    hasFlight,
    hasHotelReservation,
    hasAnythingToPrint,
    status,
    overlays,
  };
}

